import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Card } from "./ui/card";
import { Flag, Check, XCircle, RefreshCw, ExternalLink, Trash2, MessageSquare, FileText, MessageCircle, User as UserIcon } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const REASON_LABEL = {
  spam: "Spam",
  harassment: "Harassment",
  misinformation: "Misinformation",
  inappropriate: "Inappropriate",
  impersonation: "Impersonation",
  doxxing: "Doxxing",
  illegal: "Illegal content",
  other: "Other",
};

const TARGET_ICON = { topic: FileText, reply: MessageCircle, dm_message: MessageSquare, user: UserIcon };
const TARGET_LABEL = { topic: "Community topic", reply: "Reply", dm_message: "Direct message", user: "User profile" };

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function AdminReportsCard() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("open");

  // Deep-link support: /admin/settings#report-<id> scrolls to + highlights the row.
  useEffect(() => {
    const hash = (typeof window !== "undefined" && window.location.hash) || "";
    const m = hash.match(/^#report-([a-f0-9]{24})$/i);
    if (!m || items.length === 0) return;
    const el = document.querySelector(`[data-testid="report-row-${m[1]}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-2", "ring-[#0D9488]");
      setTimeout(() => el.classList.remove("ring-2", "ring-[#0D9488]"), 2500);
    }
  }, [items]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/reports", { params: { status: filter } });
      setItems(data.items || []);
      setCounts(data.counts || {});
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const update = async (id, status) => {
    try {
      await api.patch(`/admin/reports/${id}`, { status });
      toast.success(`Report ${status}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const removeItem = async (r) => {
    const label = TARGET_LABEL[r.target_type] || r.target_type;
    if (!window.confirm(`Delete this ${label} from the site? This cannot be undone.`)) return;
    try {
      const { data } = await api.post(`/admin/reports/${r.id}/remove`);
      toast.success(data.deleted ? `${label} deleted` : "Report closed (item was already gone)");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Card className="bg-white border border-slate-200 rounded-none p-6" data-testid="admin-reports-card">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <Flag className="w-4 h-4 text-red-400" />
        <div>
          <div className="font-mono-num text-[11px] uppercase tracking-[0.3em] text-red-400">// Moderation</div>
          <div className="font-display font-black uppercase tracking-tight text-xl">User Reports</div>
        </div>
        <div className="ml-auto flex items-center gap-1">
          {["open", "resolved", "dismissed", "removed", "all"].map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              data-testid={`reports-filter-${f}`}
              className={`px-2 h-7 text-[10px] uppercase tracking-widest font-mono-num border transition-colors ${
                filter === f ? "border-[#0D9488] bg-[#0D9488]/[0.05] text-[#0D9488]" : "border-slate-200 text-slate-600 hover:text-slate-900"
              }`}
            >
              {f} {f !== "all" && counts[f] ? `(${counts[f]})` : ""}
            </button>
          ))}
          <button type="button" onClick={load} disabled={loading} title="Refresh" className="w-7 h-7 flex items-center justify-center border border-slate-200 hover:border-[#0D9488] hover:text-[#0D9488] text-slate-500 disabled:opacity-50" data-testid="reports-refresh">
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-slate-500 text-sm font-mono-num uppercase tracking-widest animate-pulse">Loading…</div>
      ) : items.length === 0 ? (
        <div className="border border-slate-200 bg-slate-50 p-6 text-center text-slate-500 text-sm" data-testid="reports-empty">
          Nothing here — the {filter === "all" ? "" : filter} queue is empty.
        </div>
      ) : (
        <ul className="border border-slate-200 divide-y divide-zinc-900" data-testid="reports-list">
          {items.map((r) => {
            const TargetIcon = TARGET_ICON[r.target_type] || Flag;
            const canRemove = r.status === "open" && r.target_type !== "user";
            const contextHref = r.target_type === "reply" && r.topic_id
              ? `/community/${r.topic_id}#reply-${r.target_id}`
              : r.target_type === "topic" ? `/community/${r.target_id}`
              : r.target_type === "dm_message" && r.thread_id ? `/messages?thread=${r.thread_id}`
              : r.target_type === "user" ? `/u/${r.target_id}`
              : r.context_url;
            return (
            <li key={r.id} className={`px-4 py-3 ${r.urgent && r.status === "open" ? "bg-red-50/40 border-l-4 border-red-500" : ""}`} data-testid={`report-row-${r.id}`}>
              <div className="flex items-center gap-2 flex-wrap mb-1.5">
                {r.urgent && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-mono-num uppercase tracking-widest font-black text-white bg-red-600 border border-red-700 px-1.5 py-0.5" data-testid={`report-urgent-${r.id}`}>
                    ⚠ Urgent
                  </span>
                )}
                <span className="inline-flex items-center gap-1 text-[10px] font-mono-num uppercase tracking-widest border border-slate-200 text-slate-700 px-1.5 py-0.5">
                  <TargetIcon className="w-3 h-3" /> {TARGET_LABEL[r.target_type] || r.target_type}
                </span>
                <span className={`text-[10px] font-mono-num uppercase tracking-widest px-1.5 py-0.5 border ${r.urgent ? "border-red-500 bg-red-500 text-white font-black" : "border-red-500/40 text-red-500"}`}>{REASON_LABEL[r.reason] || r.reason}</span>
                <span className="text-[10px] font-mono-num text-slate-500 ml-auto">{timeAgo(r.created_at)}</span>
                {r.status !== "open" && (
                  <span className={`text-[10px] font-mono-num uppercase tracking-widest px-1.5 py-0.5 border ${
                    r.status === "resolved" ? "border-emerald-500/40 text-emerald-500"
                    : r.status === "removed" ? "border-red-500/40 text-red-500"
                    : "border-slate-300 text-slate-500"
                  }`}>{r.status}</span>
                )}
              </div>
              {r.target_snippet && (
                <blockquote className="border-l-2 border-slate-300 pl-3 py-1 my-1.5 bg-slate-50 text-sm text-slate-700 whitespace-pre-wrap line-clamp-3" data-testid={`report-snippet-${r.id}`}>
                  {r.target_snippet}
                </blockquote>
              )}
              <div className="text-xs text-slate-600 mb-1">
                By <span className="font-mono-num text-slate-800">{r.target_author_name || "Unknown"}</span>
                <span className="mx-1.5 text-slate-300">·</span>
                Reported by <span className="font-mono-num text-slate-800">{r.reporter_name}</span>
              </div>
              {r.notes && <div className="text-sm text-slate-600 mb-2 whitespace-pre-wrap">{r.notes}</div>}
              <div className="flex items-center gap-2 flex-wrap">
                {contextHref && (
                  <Link to={contextHref} className="inline-flex items-center gap-1 text-[10px] font-mono-num text-[#0D9488] hover:underline uppercase tracking-widest" data-testid={`report-link-${r.id}`}>
                    <ExternalLink className="w-3 h-3" /> View context
                  </Link>
                )}
                <div className="ml-auto flex items-center gap-1.5">
                  {r.status === "open" && (
                    <>
                      <button type="button" onClick={() => update(r.id, "dismissed")} data-testid={`report-dismiss-${r.id}`} className="inline-flex items-center gap-1 border border-slate-200 hover:border-slate-500 text-slate-600 hover:text-slate-900 px-2 h-7 text-[10px] uppercase tracking-widest font-semibold">
                        <XCircle className="w-3 h-3" /> Dismiss
                      </button>
                      {canRemove && (
                        <button type="button" onClick={() => removeItem(r)} data-testid={`report-remove-${r.id}`} className="inline-flex items-center gap-1 bg-red-500 hover:bg-red-600 text-white px-2 h-7 text-[10px] uppercase tracking-widest font-semibold">
                          <Trash2 className="w-3 h-3" /> Remove
                        </button>
                      )}
                      <button type="button" onClick={() => update(r.id, "resolved")} data-testid={`report-resolve-${r.id}`} className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white px-2 h-7 text-[10px] uppercase tracking-widest font-semibold">
                        <Check className="w-3 h-3" /> Resolve
                      </button>
                    </>
                  )}
                </div>
              </div>
            </li>
          );})}
        </ul>
      )}
    </Card>
  );
}
